import React, { useEffect, useState } from 'react'

import classNames from 'classnames'
import ReactMarkdown from 'react-markdown'
import rehypeSanitize from 'rehype-sanitize'
import remarkGfm from 'remark-gfm'

export interface Accordion2Props {
  markdownContent: string
  className?: string
  titleClassName?: string
  contentClassName?: string
}

interface AccordionItem {
  title: string
  content: string
}

const Accordion2: React.FC<Accordion2Props> = ({
  markdownContent,
  className,
  titleClassName,
  contentClassName,
}) => {
  const [accordionItems, setAccordionItems] = useState<AccordionItem[]>([])
  const [openIndex, setOpenIndex] = useState<number>(-1)

  useEffect(() => {
    if (markdownContent) {
      const items = parseMarkdownToAccordionItems(markdownContent)
      setAccordionItems(items)
      if (items.length > 0) {
        setOpenIndex(0) // Open first item by default
      }
    }
  }, [markdownContent])

  const parseMarkdownToAccordionItems = (markdown: string): AccordionItem[] => {
    // Split by ## headings
    const sections = markdown.split(/^## /m).filter(Boolean)

    return sections.map(section => {
      // For each section, the first line is the title
      const lines = section.split('\n')
      const title = lines[0].trim()

      // The rest is the content
      const content = lines.slice(1).join('\n').trim()

      return { title, content }
    })
  }

  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? -1 : index)
  }

  const containerClasses = classNames(
    'transition-all duration-300 ease-in-out',
    className,
  )

  const titleClasses = classNames(
    'flex justify-between items-center',
    'py-4 px-2 w-full text-left',
    'font-semibold text-lg md:text-xl',
    'cursor-pointer',
    'hover:text-blue-800',
    'transition-colors duration-300',
    titleClassName,
  )

  const contentClasses = classNames(
    'overflow-hidden transition-all duration-300 ease-in-out',
    'text-gray-700 pl-16 pr-2 pb-4',
    contentClassName,
  )

  return (
    <div className="accordion-container">
      {accordionItems.map((item, index) => (
        <div key={index} className={containerClasses}>
          <button
            className={titleClasses}
            onClick={() => handleToggle(index)}
            aria-expanded={openIndex === index}
          >
            <div className="flex items-center">
              <div
                className={classNames(
                  'flex items-center justify-center',
                  'w-10 h-10 rounded-full',
                  'bg-blue-800 text-white',
                  'mr-4 flex-shrink-0',
                  'transition-transform duration-300',
                )}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v12M6 12h12"
                  />
                </svg>
              </div>
              <span>{item.title}</span>
            </div>
            <span
              className={classNames(
                'transform transition-transform duration-300',
                {
                  'rotate-180': openIndex === index,
                },
              )}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </span>
          </button>
          <div
            className={classNames(contentClasses, {
              'max-h-0 opacity-0': openIndex !== index,
              'max-h-none opacity-100': openIndex === index,
            })}
          >
            <article className="prose prose-lg text-left">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeSanitize]}
                components={{
                  h1: ({ ...props }) => (
                    <h1 {...props} className="text-blue-800" />
                  ),
                  p: ({ ...props }) => (
                    <p {...props} className="text-gray-500 text-left" />
                  ),
                }}
              >
                {item.content}
              </ReactMarkdown>
            </article>
          </div>
        </div>
      ))}
    </div>
  )
}

export default Accordion2
